import { fetchSchoolInfo } from '@/services/schoolInfoService';
import { getCurrentDate, convertToArabicNumbers } from '@/utils/pdf/pdfUtils';
import { renderSeparatePagesPDF } from './pdf/multiPageRenderer';

interface StudentLateData {
  student: {
    id: string;
    name: string;
    grade: string;
    section: string;
    student_id: string;
  };
  period: string;
  totalDays: number;
  lateCount: number;
  latePercentage: number;
  lateDays: Array<{
    date: string;
    day: string;
    hijriDate: string;
    notes: string;
  }>;
}

export const generateStudentLatePDF = async (
  studentData: StudentLateData
) => {
  try {
    console.log('🔥 studentLatePdfGenerator: بدء العملية');
    console.log('📊 بيانات الطالب:', studentData.student.name);
    
    console.log('🏫 جلب معلومات المدرسة...');
    const schoolInfo = await fetchSchoolInfo();
    console.log('✅ تم جلب معلومات المدرسة:', schoolInfo);
    
    console.log('📋 استخدام شعار وزارة التعليم...');
    const ministryLogoUrl = '/lovable-uploads/9af71c79-dc3c-4b30-b0d5-466d18153e1e.png';
    console.log('✅ تم تحديد شعار وزارة التعليم');
    
    const currentDate = getCurrentDate();
    console.log('📅 التاريخ الحالي:', currentDate);
    
    console.log('📄 تقسيم المحتوى إلى صفحات منفصلة...');
    
    // الصفحة الأولى - تقرير التأخر
    const page1Html = generateLateFirstPageHtml(schoolInfo, studentData, ministryLogoUrl, currentDate);
    
    // الصفحة الثانية - الرسم البياني  
    const page2Html = generateLateChartPageHtml(schoolInfo, studentData, ministryLogoUrl, currentDate);
    
    await renderSeparatePagesPDF([page1Html, page2Html], `تقرير_تأخر_${studentData.student.name}.pdf`);
    
    console.log('✅ تم تحويل HTML إلى PDF بنجاح!');
    
  } catch (error) {
    console.error('❌ خطأ في إنشاء PDF:', error);
    throw error;
  }
};

// دالة لإنشاء الصفحة الأولى - تقرير التأخر
const generateLateFirstPageHtml = (
  schoolInfo: any,
  studentData: StudentLateData,
  ministryLogoUrl: string,
  currentDate: string
): string => {
  return `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>تقرير تأخر الطالب</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            direction: rtl;
            background-color: #f8f9fa;
            padding: 30px;
            color: #333;
            font-size: 12px;
            font-weight: normal;
            text-align: justify;
        }
        
        .page-container {
            max-width: 210mm;
            margin: 0 auto;
            background-color: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            text-align: justify;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #2E8B7B;
            padding-bottom: 20px;
        }
        
        .student-info-header {
            flex: 1;
            text-align: right;
        }
        
        .student-field {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-bottom: 6px;
            line-height: 1.4;
        }
        
        .school-info-section {
            flex: 1;
            text-align: right;
        }
        
        .school-field {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-bottom: 8px;
            line-height: 1.4;
        }
        
        .logo-section {
            flex: 1;
            text-align: center;
        }
        
        .ministry-logo {
            width: 120px;
            height: 80px;
            object-fit: contain;
        }
        
        .stats-section {
            margin-bottom: 20px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
        }
        
        .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #2E8B7B;
            margin-bottom: 20px;
            text-align: center;
            padding: 10px;
            background: linear-gradient(135deg, #f1f8ff, #e8f5e8);
            border-radius: 8px;
        }
        
        .stats-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow: hidden;
            font-size: 14px;
            margin-left: auto;
            margin-right: auto;
        }
        
        .stats-table th {
            background: #0DA9A6;
            color: white;
            font-weight: bold;
            padding: 8px 16px;
            text-align: center;
            font-size: 14px;
            border: 1px solid #d0d0d0;
        }
        
        .stats-table td {
            padding: 8px 16px;
            text-align: center;
            border: 1px solid #d0d0d0;
            background: white;
            font-size: 14px;
            font-weight: normal;
        }
        
        .stats-table tr:nth-child(even) td {
            background: #f8f9fa;
        }
        
        .stat-number {
            font-size: 20px;
            font-weight: bold;
        }
        
        .stat-number.total { color: #3498db; }
        .stat-number.present { color: #27ae60; }
        .stat-number.late { color: #2E8B7B; }
        
        .late-percentage {
            color: #2E8B7B;
            font-weight: bold;
        }
        
        .details-section {
            margin-top: 30px;
        }
        
        .details-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            box-shadow: 0 4px 10px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow: hidden;
            margin-left: auto;
            margin-right: auto;
        }
        
        .details-table th {
            background: #0DA9A6;
            color: white;
            font-weight: bold;
            padding: 8px 10px;
            text-align: center;
            font-size: 14px;
            border: 1px solid #d0d0d0;
        }
        
        .details-table td {
            padding: 6px 10px;
            text-align: center;
            border: 1px solid #d0d0d0;
            background: white;
        }
        
        .details-table tr:nth-child(even) td {
            background: #f8f9fa;
        }
        
        .late-status {
            background: #fff3cd;
            color: #856404;
            padding: 6px 12px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 12px;
        }
        
        .row-number {
            background: linear-gradient(135deg, #f39c12, #e67e22);
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 12px;
        }
        
        .footer {
            margin-top: 40px;
            text-align: center;
            padding: 20px;
            border-top: 2px solid #e9ecef;
            color: #666;
        }
        
        .generation-date {
            font-size: 12px;
            margin-top: 10px;
        }
        
        .no-data {
            text-align: center;
            color: #666;
            font-style: normal;
            padding: 40px;
            background: #f8f9fa;
            border-radius: 8px;
            margin: 20px 0;
        }
        
        .no-data h3 {
            margin-bottom: 15px;
            font-size: 18px;
            font-weight: bold;
        }
        
        .no-data p {
            margin: 0;
            font-size: 14px;
            color: #888;
        }
    </style>
</head>
<body>
    <div class="page-container">
        <!-- Header -->
        <div class="header">
            <div class="school-info-section">
                <div class="school-field">${schoolInfo.country || 'المملكة العربية السعودية'}</div>
                <div class="school-field">${schoolInfo.ministry || 'وزارة التعليم'}</div>
                <div class="school-field">${schoolInfo.education_department || 'إدارة التعليم'}</div>
                <div class="school-field">${schoolInfo.school_name || 'اسم المدرسة'}</div>
            </div>
            
            <div class="logo-section">
                <img src="${ministryLogoUrl}" alt="شعار وزارة التعليم" class="ministry-logo">
            </div>
            
            <div class="student-info-header">
                <div class="student-field"><strong>الطالب:</strong> ${studentData.student.name}</div>
                <div class="student-field"><strong>الصف:</strong> ${studentData.student.grade}</div>
                <div class="student-field"><strong>الشعبة:</strong> ${studentData.student.section}</div>
                <div class="student-field"><strong>الفترة:</strong> ${studentData.period}</div>
            </div>
        </div>

        <!-- Statistics Table -->
        <div class="stats-section">
            <div class="section-title">إحصائيات التأخر</div>
            <table class="stats-table">
                <thead>
                    <tr>
                        <th>البيان</th>
                        <th>العدد</th>
                        <th>النسبة</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>إجمالي الأيام</strong></td>
                        <td><span class="stat-number total">${convertToArabicNumbers(studentData.totalDays.toString())}</span></td>
                        <td>${convertToArabicNumbers('100')}%</td>
                    </tr>
                    <tr>
                        <td><strong>الحضور في الموعد</strong></td>
                        <td><span class="stat-number present">${convertToArabicNumbers((studentData.totalDays - studentData.lateCount).toString())}</span></td>
                        <td>${convertToArabicNumbers((((studentData.totalDays - studentData.lateCount) / studentData.totalDays) * 100).toFixed(1))}%</td>
                    </tr>
                    <tr>
                        <td><strong>مرات التأخر</strong></td>
                        <td><span class="stat-number late">${convertToArabicNumbers(studentData.lateCount.toString())}</span></td>
                        <td><span class="late-percentage">${convertToArabicNumbers(studentData.latePercentage.toString())}%</span></td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Late Details -->
        ${studentData.lateDays.length > 0 ? `
        <div class="details-section">
            <div class="section-title">تفاصيل أيام التأخر</div>
            <table class="details-table">
                <thead>
                    <tr>
                        <th style="width: 8%">م</th>
                        <th style="width: 15%">اليوم</th>
                        <th style="width: 25%">التاريخ الهجري</th>
                        <th style="width: 15%">الحالة</th>
                        <th style="width: 37%">ملاحظات</th>
                    </tr>
                </thead>
                <tbody>
                    ${studentData.lateDays.map((day, index) => `
                    <tr>
                        <td><span class="row-number">${convertToArabicNumbers((index + 1).toString())}</span></td>
                        <td><strong>${day.day}</strong></td>
                        <td>${day.hijriDate}</td>
                        <td><span class="late-status">متأخر</span></td>
                        <td>${day.notes || 'بدون ملاحظات'}</td>
                    </tr>
                    `).join('')}
                </tbody>
            </table>
        </div>
        ` : `
        <div class="no-data">
            <h3>لا توجد أيام تأخر في الفترة المحددة</h3>
            <p>الطالب لم يتأخر في هذه الفترة</p>
        </div>
        `}

        <!-- Footer -->
        <div class="footer">
            <p>تم إنشاء هذا التقرير بواسطة نظام إدارة الحضور</p>
            <div class="generation-date">تاريخ الإنشاء: ${currentDate}</div>
        </div>
    </div>
</body>
</html>
  `;
};

// دالة لإنشاء صفحة الرسم البياني للتأخر
const generateLateChartPageHtml = (
  schoolInfo: any,
  studentData: StudentLateData,
  ministryLogoUrl: string,
  currentDate: string
): string => {
  return `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>الرسم البياني للحضور والتأخر</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            direction: rtl;
            background-color: #f8f9fa;
            padding: 30px;
            color: #333;
            font-size: 12px;
            font-weight: normal;
            text-align: justify;
        }
        
        .page-container {
            max-width: 210mm;
            margin: 0 auto;
            background-color: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            text-align: justify;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #2E8B7B;
            padding-bottom: 20px;
        }
        
        .student-info-header {
            flex: 1;
            text-align: right;
        }
        
        .student-field {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-bottom: 6px;
            line-height: 1.4;
        }
        
        .school-info-section {
            flex: 1;
            text-align: right;
        }
        
        .school-field {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-bottom: 8px;
            line-height: 1.4;
        }
        
        .logo-section {
            flex: 1;
            text-align: center;
        }
        
        .ministry-logo {
            width: 120px;
            height: 80px;
            object-fit: contain;
        }
        
        .charts-page-title {
            text-align: center;
            color: #174459;
            font-size: 18px;
            font-weight: 700;
            margin-bottom: 20px;
            padding: 10px;
            border-radius: 8px;
            background: linear-gradient(135deg, rgba(23, 68, 89, 0.05) 0%, rgba(13, 169, 166, 0.05) 100%);
        }
        
        .stats-cards-container {
            display: flex;
            gap: 15px;
            justify-content: center;
            margin-bottom: 30px;
            flex-wrap: wrap;
        }
        
        .stat-card {
            background: linear-gradient(135deg, #ffffff, #f8f9fa);
            padding: 15px;
            border-radius: 10px;
            text-align: center;
            border: 2px solid #e9ecef;
            min-width: 150px;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        
        .stat-content {
            flex: 1;
            text-align: center;
            padding: 0 10px;
        }
        
        .stat-number {
            font-size: 18px;
            font-weight: bold;
            text-align: left;
            min-width: 35px;
        }
        
        .ontime-card .stat-number { color: #27ae60; }
        .late-card .stat-number { color: #2E8B7B; }
        .total-card .stat-number { color: #4b5563; }
        
        .stat-label {
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 2px;
            color: #374151;
        }
        
        .stat-percentage {
            font-size: 9px;
            color: #6b7280;
            margin-top: 1px;
        }
        
        .chart-container {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 30px;
            margin-top: 20px;
            text-align: center;
        }
        
        .chart-title {
            font-size: 16px;
            font-weight: bold;
            color: #2E8B7B;
            margin-bottom: 20px;
        }
        
        .pie-chart-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
        }
        
        .pie-chart {
            width: 240px;
            height: 240px;
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
        }
        
        .chart-center-text {
            font-size: 14px;
            font-weight: 600;
            fill: #374151;
        }
        
        .chart-center-number {
            font-size: 20px;
            font-weight: 700;
            fill: #1f2937;
        }
        
        .pie-legend {
            display: flex;
            gap: 30px;
            justify-content: center;
            align-items: center;
        }
        
        .legend-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: normal;
        }
        
        .legend-color {
            width: 18px;
            height: 18px;
            border-radius: 3px;
            border: 1px solid #ddd;
        }
        
        .legend-color.green {
            background-color: #27ae60;
        }
        
        .legend-color.teal {
            background-color: #2E8B7B;
        }
        
        .footer {
            margin-top: 40px;
            text-align: center;
            padding: 20px;
            border-top: 2px solid #e9ecef;
            color: #666;
        }
        
        .generation-date {
            font-size: 12px;
            margin-top: 10px;
        }
    </style>
</head>
<body>
    <div class="page-container">
        <!-- Header -->
        <div class="header">
            <div class="school-info-section">
                <div class="school-field">${schoolInfo.country || 'المملكة العربية السعودية'}</div>
                <div class="school-field">${schoolInfo.ministry || 'وزارة التعليم'}</div>
                <div class="school-field">${schoolInfo.education_department || 'إدارة التعليم'}</div>
                <div class="school-field">${schoolInfo.school_name || 'اسم المدرسة'}</div>
            </div>
            
            <div class="logo-section">
                <img src="${ministryLogoUrl}" alt="شعار وزارة التعليم" class="ministry-logo">
            </div>
            
            <div class="student-info-header">
                <div class="student-field"><strong>الطالب:</strong> ${studentData.student.name}</div>
                <div class="student-field"><strong>الصف:</strong> ${studentData.student.grade}</div>
                <div class="student-field"><strong>الشعبة:</strong> ${studentData.student.section}</div>
                <div class="student-field"><strong>الفترة:</strong> ${studentData.period}</div>
            </div>
        </div>
        
        <div class="charts-page-title">الرسم البياني للحضور والتأخر من ${studentData.period}</div>
        
        <!-- البطاقات الإحصائية -->
        <div class="stats-cards-container">
            <div class="stat-card ontime-card">
                <div class="stat-content">
                    <div class="stat-label">الحضور في الموعد</div>
                    <div class="stat-percentage">${convertToArabicNumbers((((studentData.totalDays - studentData.lateCount) / studentData.totalDays) * 100).toFixed(1))}% من المجموع</div>
                </div>
                <div class="stat-number">${convertToArabicNumbers((studentData.totalDays - studentData.lateCount).toString())}</div>
            </div>
            
            <div class="stat-card late-card">
                <div class="stat-content">
                    <div class="stat-label">أيام التأخر</div>
                    <div class="stat-percentage">${convertToArabicNumbers(studentData.latePercentage.toString())}% من المجموع</div>
                </div>
                <div class="stat-number">${convertToArabicNumbers(studentData.lateCount.toString())}</div>
            </div>
            
            <div class="stat-card total-card">
                <div class="stat-content">
                    <div class="stat-label">إجمالي الأيام</div>
                    <div class="stat-percentage">${convertToArabicNumbers('100')}%</div>
                </div>
                <div class="stat-number">${convertToArabicNumbers(studentData.totalDays.toString())}</div>
            </div>
        </div>
        
        <!-- الرسم البياني الدائري -->
        <div class="chart-container">
            <h3 class="chart-title">مقارنة الحضور في الموعد والتأخر</h3>
            <div class="pie-chart-container">
                <svg class="pie-chart" viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg">
                    <!-- دائرة الخلفية -->
                    <circle cx="120" cy="120" r="90" fill="#f0f0f0" stroke="#ddd" stroke-width="2"/>
                    
                    <!-- قسم الحضور في الموعد -->
                    <circle cx="120" cy="120" r="90" fill="none" stroke="#27ae60" stroke-width="20"
                            stroke-dasharray="${((((studentData.totalDays - studentData.lateCount) / studentData.totalDays) * 100) * 565.48) / 100} 565.48"
                            stroke-dashoffset="0" transform="rotate(-90 120 120)"/>
                    
                    <!-- قسم التأخر -->
                    <circle cx="120" cy="120" r="90" fill="none" stroke="#2E8B7B" stroke-width="20"
                            stroke-dasharray="${(studentData.latePercentage * 565.48) / 100} 565.48"
                            stroke-dashoffset="-${((((studentData.totalDays - studentData.lateCount) / studentData.totalDays) * 100) * 565.48) / 100}" transform="rotate(-90 120 120)"/>
                    
                    <!-- النصوص داخل الدائرة -->
                    <text x="120" y="115" text-anchor="middle" class="chart-center-text">المجموع</text>
                    <text x="120" y="135" text-anchor="middle" class="chart-center-number">${convertToArabicNumbers(studentData.totalDays.toString())}</text>
                </svg>
                
                <div class="pie-legend">
                    <div class="legend-item">
                        <div class="legend-color green"></div>
                        <span>في الموعد: ${convertToArabicNumbers((studentData.totalDays - studentData.lateCount).toString())} يوم (${convertToArabicNumbers((((studentData.totalDays - studentData.lateCount) / studentData.totalDays) * 100).toFixed(1))}%)</span>
                    </div>
                    <div class="legend-item">
                        <div class="legend-color teal"></div>
                        <span>تأخر: ${convertToArabicNumbers(studentData.lateCount.toString())} يوم (${convertToArabicNumbers(studentData.latePercentage.toString())}%)</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer for Chart Page -->
        <div class="footer">
            <p>تم إنشاء هذا التقرير بواسطة نظام إدارة الحضور</p>
            <div class="generation-date">تاريخ الإنشاء: ${currentDate}</div>
        </div>
    </div>
</body>
</html>
  `;
};